import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { _throw } from 'rxjs/observable/throw';
import { AuthenticationRequest, AuthenticationResponse } from '../../models/auth.models';
import { AppConfig } from '../../../app.config';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  PopupRequest,
  SilentRequest,
  SsoSilentRequest
} from '@azure/msal-browser';
import { finalize, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authStateChange$: EventEmitter<boolean> = new EventEmitter();
  private tokenAcquired$: EventEmitter<string> = new EventEmitter();
  private interactiveAuthentication$: Observable<string>;

  get isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private readonly AUTH_TOKEN_KEY: string = 'auth_token';
  private readonly REFRESH_TOKEN_KEY: string = 'refresh_token';
  private readonly USER_KEY: string = 'auth_user';
  private readonly USERNAME_KEY: string = 'auth_username';
  private readonly REMEMBER_KEY: string = 'auth_remember';
  private readonly MSAL_ACCOUNT_USERNAME_KEY: string = 'auth_msal_account_username';
  private readonly MSAL_ACCOUNT_NAME_KEY: string = 'auth_msal_account_name';
  private readonly LOGGED_OUT_KEY: string = 'auth_logged_out';

  private user: any;

  constructor(
    private http: HttpClient,
    private msalService: MsalService
  ) {
    if (!AppConfig)
      throw new Error('AuthService requires config to be configured as an IAppConfig implementation using the APP_CONFIG provider token');
    else if (!AppConfig.settings.auth) {
      // tslint:disable-next-line:max-line-length
      throw new Error('AuthService requires authConfig to be configured in an IAppConfig implementation using the APP_CONFIG provider token');
    } else if (!AppConfig.settings.auth.defaultRoute || !AppConfig.settings.auth.loginRoute) {
      throw new Error('authConfig for APP_CONFIG provider token must have both defaultRoute and loginRoute set');
    }

    this.registerMsalTokenEvents();
  }

  currentUser<TUser>(): TUser {
    if (!this.user) {
      this.user = this.getStoredUser();
    }
    return this.user;
  }

  login<TUser>(request: AuthenticationRequest): Observable<AuthenticationResponse<TUser>> {
    const url = `${AppConfig.settings.baseApiUrl}${AppConfig.settings.auth.authTokenUrl}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http
      .post<AuthenticationResponse<TUser>>(url, this.getApiLoginRequest(request), { headers: headers })
      .mergeMap(res => {
        this.clearLoggedOut();
        this.saveAuthenticationResponse(res);
        this.setRememberedUser(request);
        this.authStateChange$.emit(this.isAuthenticated);
        return Observable.of(res);
      })
      .catch((err) => {
        err = !!err.error && (typeof(err.error) === 'object') ? err.error : err;
        return _throw(err);
      });
  }

  loginWithPopup(): Observable<string> {
    const request: PopupRequest = {
      scopes: [AppConfig.settings.auth.azureADScope],
      prompt: 'consent',
    };

    return this.msalService
      .loginPopup(request)
      .mergeMap((result: AuthenticationResult) => {
        this.clearLoggedOut();
        this.saveMsalAuthenticationResult(result);

        if (this.getToken()) {
          this.authStateChange$.emit(true);
          return Observable.of(this.getToken());
        }

        return this.refreshAccessTokenFromMsal();
      })
      .catch(err => _throw(err));
  }

  reauthenticateWithPopup(): Observable<string> {
    if (this.isLoggedOut()) {
      return _throw('User has logged out.');
    }

    if (this.interactiveAuthentication$) {
      return this.interactiveAuthentication$;
    }

    const account = this.getMsalAccount();
    const request: PopupRequest = {
      scopes: [AppConfig.settings.auth.azureADScope]
    };

    if (account) {
      request.account = account;
    } else {
      const username = localStorage.getItem(this.MSAL_ACCOUNT_USERNAME_KEY);
      if (username) {
        request.loginHint = username;
      }
    }

    const authentication$ = account
      ? this.msalService.acquireTokenPopup(request)
      : this.msalService.loginPopup(request);

    this.interactiveAuthentication$ = authentication$
      .map((result: AuthenticationResult) => this.saveMsalResultAndReturnAccessToken(result))
      .pipe(
        finalize(() => this.interactiveAuthentication$ = null),
        shareReplay(1)
      );

    return this.interactiveAuthentication$;
  }

  restoreAuthenticatedSession(): Observable<boolean> {
    if (this.isAuthenticated) {
      return Observable.of(true);
    }

    if (this.isLoggedOut()) {
      return Observable.of(false);
    }

    return this.refreshAccessTokenFromMsal()
      .map(() => true)
      .catch(() => Observable.of(false));
  }

  refreshAccessToken(): Observable<string> {
    if (this.isLoggedOut()) {
      return _throw('User has logged out.');
    }

    const refreshToken = this.getRefreshToken();
    const previousToken = this.getToken();

    // Wait for MSAL, its hidden iframe, or localStorage to provide the replacement access token.
    return this.refreshAccessTokenFromMsalOrEvent(previousToken)
      .catch(err => {
        if (refreshToken) {
          return this.refreshAccessTokenFromApi(refreshToken);
        }

        return _throw(err);
      });
  }

  private refreshAccessTokenFromMsalOrEvent(previousToken: string): Observable<string> {
    return Observable.create(observer => {
      let completed = false;
      let timeout;
      let tokenPoll;
      let tokenSubscription;
      let msalSubscription;
      let ssoSubscription;

      const cleanup = () => {
        if (timeout) {
          clearTimeout(timeout);
        }

        if (tokenPoll) {
          clearInterval(tokenPoll);
        }

        if (tokenSubscription) {
          tokenSubscription.unsubscribe();
        }

        if (msalSubscription) {
          msalSubscription.unsubscribe();
        }

        if (ssoSubscription) {
          ssoSubscription.unsubscribe();
        }
      };

      const completeWithToken = (accessToken: string) => {
        if (this.isLoggedOut()) {
          completed = true;
          cleanup();
          observer.error('User has logged out.');
          return;
        }

        if (completed || !accessToken || accessToken === previousToken) {
          return;
        }

        completed = true;
        cleanup();
        observer.next(accessToken);
        observer.complete();
      };

      const startSsoFallback = () => {
        if (completed || ssoSubscription) {
          return;
        }

        ssoSubscription = this.refreshAccessTokenFromSsoSilent()
          .subscribe(
            token => completeWithToken(token),
            () => {
              // The MSAL success event can still arrive after this error.
            }
          );
      };

      tokenSubscription = this.tokenAcquired$.subscribe((accessToken: string) => completeWithToken(accessToken));

      // Hidden iframe renewals can update localStorage without notifying this app instance.
      tokenPoll = setInterval(() => completeWithToken(this.getToken()), 250);
      completeWithToken(this.getToken());

      timeout = setTimeout(() => {
        if (completed) {
          return;
        }

        completed = true;
        cleanup();
        observer.error('Timed out waiting for refreshed access token.');
      }, 15000);

      msalSubscription = this.refreshAccessTokenFromMsal(true)
        .subscribe(
          token => completeWithToken(token),
          () => startSsoFallback()
        );

      return cleanup;
    });
  }

  private refreshAccessTokenFromSsoSilent(): Observable<string> {
    if (this.isLoggedOut()) {
      return _throw('User has logged out.');
    }

    const account = this.getMsalAccount();

    if (!account) {
      return _throw('No account is available for silent SSO token refresh.');
    }

    // Fallback for when the refresh-token grant fails but browser SSO can still issue a token.
    const request: SsoSilentRequest = {
      scopes: [AppConfig.settings.auth.azureADScope],
      loginHint: account.username
    };

    return this.msalService
      .ssoSilent(request)
      .map((result: AuthenticationResult) => this.saveMsalResultAndReturnAccessToken(result));
  }

  private saveMsalResultAndReturnAccessToken(result: AuthenticationResult): string {
    const accessToken = this.getAccessTokenFromMsalResult(result);

    if (!accessToken) {
      throw new Error('Token refresh completed without an access token.');
    }

    if (this.isLoggedOut()) {
      throw new Error('User has logged out.');
    }

    this.saveMsalAuthenticationResult(result);
    this.authStateChange$.emit(this.isAuthenticated);
    return accessToken;
  }

  private refreshAccessTokenFromApi(refreshToken: string): Observable<string> {
    const url = `${AppConfig.settings.baseApiUrl}${AppConfig.settings.auth.authTokenUrl}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`;

    return this.http
      .post<AuthenticationResponse<any>>(url, body, { headers: headers })
      .map(res => {
        if (this.isLoggedOut()) {
          throw new Error('User has logged out.');
        }

        this.saveAuthenticationResponse(res);
        this.authStateChange$.emit(this.isAuthenticated);
        return res.access_token;
      });
  }

  private refreshAccessTokenFromMsal(forceRefresh: boolean = false): Observable<string> {
    if (this.isLoggedOut()) {
      return _throw('User has logged out.');
    }

    const account = this.getMsalAccount();

    if (!account) {
      return _throw('No account is available for silent token refresh.');
    }

    const request: SilentRequest = {
      scopes: [AppConfig.settings.auth.azureADScope],
      account: account,
      forceRefresh: forceRefresh
    };

    return this.msalService
      .acquireTokenSilent(request)
      .map((result: AuthenticationResult) => this.saveMsalResultAndReturnAccessToken(result));
  }

  logout(): void {
    const wasAuthenticated = this.isAuthenticated;
    this.setLoggedOut();

    localStorage.removeItem(this.AUTH_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.user = null;

    if (this.msalService && this.msalService.instance) {
      // Clear cached MSAL token material while leaving the saved display name available.
      this.msalService.instance.setActiveAccount(null);
      const clearCache = (this.msalService.instance as any).clearCache;

      if (clearCache) {
        const clearCacheResult = clearCache.call(this.msalService.instance);

        if (clearCacheResult && clearCacheResult.catch) {
          clearCacheResult.catch(() => null);
        }
      }
    }

    if (wasAuthenticated) {
      this.authStateChange$.emit(this.isAuthenticated);
    }
  }

  getAuthHeader(): string {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }

  latestUsername(): string {
    return localStorage.getItem(this.USERNAME_KEY);
  }

  public saveToken(token: string): void {
    if (token) {
      localStorage.setItem(this.AUTH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(this.AUTH_TOKEN_KEY);
    }
  }

  public currentUserDisplayName(): string {
    const currentUser = this.currentUser<any>();

    if (currentUser) {
      const fullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
      return fullName || currentUser.username;
    }

    return localStorage.getItem(this.MSAL_ACCOUNT_NAME_KEY)
      || localStorage.getItem(this.MSAL_ACCOUNT_USERNAME_KEY)
      || 'Signed in';
  }

  private saveAuthenticationResponse<TUser>(res: AuthenticationResponse<TUser>): void {
    if (this.isLoggedOut()) {
      return;
    }

    this.saveToken(res.access_token);

    if (res.refresh_token) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, res.refresh_token);
    }

    if (res.user) {
      this.setUser(res.user);
    }
  }

  private saveMsalAuthenticationResult(result: AuthenticationResult): string {
    if (!result || this.isLoggedOut()) {
      return null;
    }

    const accessToken = this.getAccessTokenFromMsalResult(result);

    if (accessToken) {
      this.saveToken(accessToken);
    }

    if (result.account) {
      this.setMsalAccount(result.account);
    }

    return accessToken;
  }

  private registerMsalTokenEvents(): void {
    if (!this.msalService || !this.msalService.instance) {
      return;
    }

    this.msalService.instance.addEventCallback((message: EventMessage) => {
      if (this.isLoggedOut()) {
        return;
      }

      if (
        message.eventType !== EventType.LOGIN_SUCCESS &&
        message.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS &&
        message.eventType !== EventType.SSO_SILENT_SUCCESS
      ) {
        return;
      }

      // Capture tokens from login, silent refresh, and hidden iframe SSO completions.
      const accessToken = this.saveMsalAuthenticationResult(message.payload as AuthenticationResult);
      this.authStateChange$.emit(this.isAuthenticated);

      if (accessToken) {
        this.tokenAcquired$.emit(accessToken);
      }
    });
  }

  private getAccessTokenFromMsalResult(result: AuthenticationResult): string {
    return result
      ? result.accessToken || (result as any).access_token
      : null;
  }

  private getToken(): string {
    return localStorage.getItem(this.AUTH_TOKEN_KEY);
  }

  private getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private isLoggedOut(): boolean {
    return localStorage.getItem(this.LOGGED_OUT_KEY) === 'true';
  }

  private setLoggedOut(): void {
    localStorage.setItem(this.LOGGED_OUT_KEY, 'true');
  }

  private clearLoggedOut(): void {
    localStorage.removeItem(this.LOGGED_OUT_KEY);
  }

  private setMsalAccount(account: AccountInfo): void {
    this.msalService.instance.setActiveAccount(account);
    localStorage.setItem(this.MSAL_ACCOUNT_USERNAME_KEY, account.username);

    if (account.name) {
      localStorage.setItem(this.MSAL_ACCOUNT_NAME_KEY, account.name);
    }
  }

  private getMsalAccount(): AccountInfo {
    if (!this.msalService || !this.msalService.instance) {
      return null;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const username = localStorage.getItem(this.MSAL_ACCOUNT_USERNAME_KEY);
    if (username) {
      const usernameAccount = this.msalService.instance.getAccountByUsername(username);
      if (usernameAccount) {
        this.msalService.instance.setActiveAccount(usernameAccount);
        return usernameAccount;
      }
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    return null;
  }

  private setUser(user: any): void {
    this.user = user;
    this.setStoredUser(user);
  }

  private setStoredUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private getStoredUser(): any {
    const storedUser = localStorage.getItem(this.USER_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  }

  private setRememberedUser(request: AuthenticationRequest): void {
    localStorage.setItem(this.REMEMBER_KEY, `${request.remember}`);
    localStorage.setItem(this.USERNAME_KEY, request.username);
  }

  private getApiLoginRequest(request: AuthenticationRequest): string {
    return `grant_type=password&username=${encodeURIComponent(request.username)}&password=${encodeURIComponent(request.password)}`;
  }
}
